import { type FC, type ReactNode, useMemo } from 'react';
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { UHMember } from 'src/types/Certification';
import { formatPhoneNumber } from 'src/utils/InputValidationUtils';
import { getRoleColors } from '../memberRoleColors';
import {
  getProfileMemberFullName,
  uhmRoleLabel,
} from './profileHouseholdUtils';

interface ProfileHouseholdMemberCardProps {
  member: UHMember;
  currentUserId?: number;
  canManage?: boolean;
  onEdit?: (member: UHMember) => void;
  onRemove?: (member: UHMember) => void;
}

/**
 * Profile (UserHouseholdMember) analogue of HouseholdMemberCard. Uses the
 * Account Hub card styling so the cert-less composition flow matches the rest
 * of the profile surfaces.
 */
const ProfileHouseholdMemberCard: FC<ProfileHouseholdMemberCardProps> = ({
  member,
  currentUserId,
  canManage = true,
  onEdit,
  onRemove,
}) => {
  const theme = useTheme();
  const roleLabel = uhmRoleLabel(member.household_role);
  const colors = getRoleColors(roleLabel);
  const initials = [member.first_name, member.last_name]
    .filter((n): n is string => Boolean(n))
    .map((n) => n[0].toUpperCase())
    .join('');

  const isHead = member.household_role === 'hoh';
  const isSelf = member.user_id != null && member.user_id === currentUserId;
  const canRemove = canManage && !isHead && !isSelf && Boolean(onRemove);
  const isTappable = canManage && Boolean(onEdit);

  const contactEntries = useMemo<
    Array<{ icon: ReactNode; text: string }>
  >(() => {
    const entries: Array<{ icon: ReactNode; text: string }> = [];
    const primaryEmail = member.emails?.find(
      (e) => e.is_primary && !e.email.endsWith('@prontohousing.io')
    );
    const primaryPhone = member.phones?.find((p) => p.is_primary);
    if (primaryEmail?.email) {
      entries.push({
        icon: <EmailIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
        text: primaryEmail.email,
      });
    }
    if (primaryPhone?.phone_number) {
      const digits = primaryPhone.phone_number.replace(/\D/g, '');
      const normalized =
        digits.length === 11 && digits.startsWith('1')
          ? digits.slice(1)
          : digits;
      entries.push({
        icon: <PhoneIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
        text: formatPhoneNumber(normalized),
      });
    }
    return entries;
  }, [member.emails, member.phones]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.5}
      onClick={isTappable && onEdit ? () => onEdit(member) : undefined}
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: '16px',
        boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
        borderLeft: '4px solid',
        borderLeftColor: colors.accentColor,
        px: 2,
        py: 1.75,
        ...(isTappable && {
          cursor: 'pointer',
          '&:hover': {
            boxShadow: `0px 2px 12px ${alpha(
              theme.palette.text.secondary,
              0.18
            )}`,
          },
        }),
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          backgroundColor: colors.avatarBg,
        }}
      >
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ color: colors.avatarText, fontSize: '1rem' }}
        >
          {initials || '?'}
        </Typography>
      </Box>

      <Stack gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body1" fontWeight={600} noWrap>
          {getProfileMemberFullName(member)}
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            px: 1,
            py: 0.25,
            borderRadius: 0.5,
            backgroundColor: colors.pillBg,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.6875rem',
              fontWeight: 700,
              color: colors.pillText,
            }}
          >
            {roleLabel}
          </Typography>
        </Box>

        {contactEntries.length > 0 ? (
          contactEntries.map((entry, index) => (
            <Stack
              key={index}
              direction="row"
              alignItems="center"
              gap={0.75}
              sx={{ minWidth: 0 }}
            >
              {entry.icon}
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {entry.text}
              </Typography>
            </Stack>
          ))
        ) : (
          <Stack direction="row" alignItems="center" gap={0.75}>
            <WarningAmberIcon fontSize="small" sx={{ color: 'warning.dark' }} />
            <Typography variant="caption" color="warning.dark" fontWeight={600}>
              Add contact information
            </Typography>
          </Stack>
        )}
      </Stack>

      {canRemove && (
        <Tooltip title="Remove member" arrow>
          <IconButton
            size="small"
            aria-label={`Remove ${getProfileMemberFullName(member)}`}
            onClick={(e) => {
              e.stopPropagation();
              onRemove?.(member);
            }}
            sx={{ color: 'error.main', flexShrink: 0 }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {isTappable && (
        <ChevronRightIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
      )}
    </Stack>
  );
};

export default ProfileHouseholdMemberCard;
